import React from 'react';
import { SetupState } from '../types';
import { themePresets } from '../data/themes';
import { Building2, Palette, ShieldCheck, ChevronRight } from 'lucide-react';

interface SuccessPopupProps {
  onComplete: () => void;
  state: SetupState;
}

/**
 * Returns the safe admin base URL from public runtime config.
 * Never contains tokens, secrets, account_id, or database_id.
 * Returns empty string if not configured or contains template placeholder.
 */
export function getSafeAdminBaseUrl(): string {
  const raw = (import.meta.env.VITE_APARTMENT_ADMIN_BASE_URL as string) || '';
  if (!raw) return '';
  // Reject template placeholders that were never filled in
  if (raw.includes('{{') || raw.includes('YOUR_') || raw === 'undefined') return '';
  // Require HTTPS (not http://localhost or bare strings)
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:') return '';
  } catch {
    return '';
  }
  return raw.replace(/\/$/, ''); // strip trailing slash
}

export const SuccessPopup: React.FC<SuccessPopupProps> = ({ onComplete, state }) => {
  const activeColorTheme = themePresets.find(t => t.id === state.themeId) || themePresets[0];

  const isLocalDev = localStorage.getItem('worker_session_token')?.startsWith('local-dev-') ?? false;
  // Real provision: apartmentLinked = true (returned from completeTenantOnboarding) AND not a local dev mock session
  const isRealProvision = state.apartmentLinked === true && !isLocalDev;



  const formattedPromptPay = (num: string) => {
    if (!num) return '-';
    const cleanNum = num.replace(/\D/g, '');
    if (cleanNum.length === 10) {
      return `XXX-XXX-${cleanNum.slice(6)}`;
    } else if (cleanNum.length === 13) {
      return `X-XXXX-XXXXX-${cleanNum.slice(10, 12)}-${cleanNum.slice(12)}`;
    }
    return 'XXX-XXX-XXXX';
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xl bg-slate-950/60 transition-all duration-300 overflow-hidden">
      
      {/* Visual styles matching the provided markup with high precision */}
      <style>{`
        .popup-radial-bg {
          background:
            radial-gradient(circle at 50% 25%, rgba(34, 197, 94, 0.18), transparent 34%),
            linear-gradient(135deg, #effdf5, #f8fafc);
        }

        .check-wrap-success {
          position: relative;
          z-index: 1;
          width: 104px;
          height: 104px;
          margin: 0 auto 22px;
          display: grid;
          place-items: center;
        }

        .check-pulse-success {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          background: #dcfce7;
          animation: pulseSuccess 1.8s ease-out infinite;
        }

        .check-ring-success {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          border: 2px solid rgba(22, 163, 74, 0.18);
          transform: scale(0.9);
          animation: ringInSuccess 700ms ease-out both;
        }

        .check-circle-success {
          position: absolute;
          inset: 10px;
          border-radius: 999px;
          background: linear-gradient(145deg, #22c55e, #15803d);
          box-shadow: 0 18px 34px rgba(22, 163, 74, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.35);
          transform: scale(0);
          animation: circlePopSuccess 520ms 120ms cubic-bezier(.2, .95, .3, 1.35) both;
        }

        .check-mark-success {
          position: relative;
          z-index: 2;
          width: 34px;
          height: 22px;
          transform: rotate(-45deg);
          margin-top: -2px;
        }

        .check-mark-success::before,
        .check-mark-success::after {
          content: "";
          position: absolute;
          background: #fff;
          border-radius: 999px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.14);
        }

        .check-mark-success::before {
          width: 6px;
          height: 0;
          left: 0;
          bottom: 0;
          animation: drawShortSuccess 220ms 520ms ease-out forwards;
        }

        .check-mark-success::after {
          width: 0;
          height: 6px;
          left: 0;
          bottom: 0;
          animation: drawLongSuccess 340ms 720ms ease-out forwards;
        }

        .loading-bar-success {
          width: min(260px, 82%);
          height: 6px;
          margin: 24px auto 0;
          border-radius: 999px;
          overflow: hidden;
          background: rgba(22, 163, 74, 0.12);
        }

        .loading-bar-success span {
          display: block;
          width: 42%;
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, rgba(34, 197, 94, 0.45), #16a34a);
          animation: loadingBarRun 1.25s ease-in-out infinite;
        }

        .popup-container-success {
          width: min(100%, 500px);
          animation: popupInSuccess 540ms cubic-bezier(.18, .9, .28, 1.16) both;
          max-height: 90vh;
          overflow-y: auto;
        }

        @keyframes popupInSuccess {
          from {
            opacity: 0;
            transform: translateY(18px) scale(0.94);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes circlePopSuccess {
          0% { transform: scale(0); }
          72% { transform: scale(1.08); }
          100% { transform: scale(1); }
        }

        @keyframes ringInSuccess {
          from {
            opacity: 0;
            transform: scale(0.74);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes pulseSuccess {
          0% {
            opacity: 0.75;
            transform: scale(0.78);
          }
          70% {
            opacity: 0;
            transform: scale(1.28);
          }
          100% {
            opacity: 0;
            transform: scale(1.28);
          }
        }

        @keyframes drawShortSuccess {
          from { height: 0; }
          to { height: 17px; }
        }

        @keyframes drawLongSuccess {
          from { width: 0; }
          to { width: 34px; }
        }

        @keyframes loadingBarRun {
          0% { transform: translateX(-110%); }
          100% { transform: translateX(260%); }
        }
      `}</style>

      {/* Main Card View */}
      <main className="popup-container-success relative overflow-hidden p-8 sm:p-11 rounded-[28px] bg-white border border-white shadow-2xl text-center backdrop-blur-xl">
        
        {/* Decorative corner glows */}
        <div className="absolute -top-[90px] -right-[90px] w-[190px] h-[190px] rounded-full bg-emerald-500/10 blur-xl pointer-events-none" />
        <div className="absolute -bottom-[110px] -left-[80px] w-[210px] h-[210px] rounded-full bg-teal-500/10 blur-xl pointer-events-none" />

        {/* Outer Ring & Check Icon Wrapper */}
        <div className="check-wrap-success" aria-hidden="true">
          <div className="check-pulse-success" />
          <div className="check-ring-success" />
          <div className="check-circle-success" />
          <div className="check-mark-success" />
        </div>

        {/* Content Details */}
        <div className="relative z-10 space-y-3 font-sans mt-2">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
            ตั้งค่าระบบสำเร็จ
          </h1>
          <p className="text-slate-500 text-sm leading-relaxed font-semibold max-w-xs mx-auto">
            {isLocalDev
              ? 'สถานะจำลอง (Local Preview) ข้อมูลถูกบันทึกเพื่อทดสอบระบบ'
              : 'หอพักและข้อมูลการรับชำระถูกบันทึกเรียบร้อย'}
          </p>

          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 mt-6 mb-4 text-left space-y-3 max-w-sm mx-auto shadow-inner">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-slate-500">ชื่อหอพัก:</span>
              <span className="text-sm font-bold text-slate-800">{state.dormName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-slate-500">สีธีมบิล:</span>
              <span className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
                <span className="w-3 h-3 rounded-full inline-block border border-black/10" style={{ backgroundColor: activeColorTheme.hex }}></span>
                {activeColorTheme.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-slate-500">PromptPay:</span>
              <span className="text-sm font-bold font-mono text-slate-800">{formattedPromptPay(state.promptPayNumber)}</span>
            </div>
            {state.logoUrl && !state.logoUrl.startsWith('blob:') && (
              <div className="text-xs text-emerald-600 font-bold bg-emerald-50 px-2 py-1 rounded inline-block mt-1">
                ✓ อัปโหลดโลโก้สำเร็จ
              </div>
            )}
          </div>


          {/* Secondary action — dismiss popup and go to dashboard */}
          <button
            type="button"
            id="success-popup-go-dashboard"
            onClick={onComplete}
            className="w-full sm:w-auto px-8 py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-sm shadow-lg shadow-emerald-200 transition-all flex items-center justify-center gap-2 mx-auto mt-4"
          >
            ไปหน้าแดชบอร์ด
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

      </main>
    </div>
  );
};
